import React, { useState, useRef, useEffect } from "react";
import appLogo from "../../../assets/images/nip-ext-logo4.png";
import signInBtn from "../../../assets/images/ButtonSignIn.png";
import showToast from "../../../utils/toastUtils";
import { BiLoaderCircle } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import { useUserLoggedin } from "../../../state/state";
import PasswordResetRequestPanel from "../PasswordReset/components/PasswordResetRequestPanel";
import { ThemeContext } from "../../../themes/ThemeContext";
import { login } from "../../../data/api/api";

const SignInPage = () => {
  const theme = React.useContext(ThemeContext);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const navigate = useNavigate();
  const [showPasswordResetPanel, setShowPasswordResetPanel] = useState(false);
  const isMountedRef = useRef(true);

  const [formData, setFormData] = useState({
    pid: "",
    password: "",
  });

  const [validationErrors, setValidationErrors] = useState({
    pid: "",
    password: "",
  });
  const [lockoutUntil, setLockoutUntil] = useState(() => {
    const stored = localStorage.getItem("loginLockoutUntil");
    return stored ? parseInt(stored, 10) : 0;
  });
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  const setUserLoggedIn = useUserLoggedin((state) => state.setUserLoggedIn);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const updateRemaining = () => {
      const now = Date.now();
      const remaining = Math.max(0, Math.ceil((lockoutUntil - now) / 1000));
      setRemainingSeconds(remaining);
      if (remaining === 0 && lockoutUntil) {
        localStorage.removeItem("loginLockoutUntil");
        setLockoutUntil(0);
      }
    };

    updateRemaining();
    const timer = setInterval(updateRemaining, 1000);
    return () => clearInterval(timer);
  }, [lockoutUntil]);

  const formatRemaining = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) {
      return `${secs}s`;
    }
    return `${mins}m ${secs.toString().padStart(2, "0")}s`;
  };

  const isLockedOut = remainingSeconds > 0;

  const validateForm = () => {
    let isValid = true;
    const errors = { pid: "", password: "" };

    if (!formData.pid.trim()) {
      errors.pid = "PID is required";
      isValid = false;
    }

    if (!formData.password.trim()) {
      errors.password = "Password is required";
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLockedOut) {
      showToast("error", `Too many attempts. Try again in ${formatRemaining(remainingSeconds)}.`, 3000);
      return;
    }

    if (!validateForm()) {
      return;
    }

    if (!isMountedRef.current) return;
    setIsLoading(true);

    try {
      const data = await login(formData);

      if (!isMountedRef.current) return;

      if (data.status === "success") {
        const { user_type, user, token } = data.data;
        
        // Store common user data
        localStorage.setItem("userLoggedin", "true");
        localStorage.setItem("userRole", user.role);
        localStorage.setItem("userType", user_type);
        localStorage.setItem("token", token);
        
        if (user_type === "partner") {
          // Partner-specific data
          localStorage.setItem("app_key", user.app_key);
          localStorage.setItem("pid", user.pid);
          localStorage.setItem("userFirstName", user.fullname);
          localStorage.setItem("acctNumber", user.virtual_account?.acctNumber);
          localStorage.setItem("businessName", user.business_name);
        } else {
          // Regular user data (support, admin, regular users)
          localStorage.setItem("pid", user.pid);
          localStorage.setItem("userFirstName", user.fullname);
          localStorage.setItem("email", user.email);
          localStorage.setItem("partnerId", user.partner_id);
          
          // Store partner information if available (for support users)
          if (user.partner) {
            localStorage.setItem("partnerBusinessName", user.partner.business_name);
            localStorage.setItem("partnerVirtualAccount", JSON.stringify(user.partner.virtual_account));
          }
        }
        
        setUserLoggedIn(true);
        showToast("success", "Login Successful", 3000);
        
        // Role-based navigation
        if (user.role === "admin") {
          navigate("/analytics");
        } else {
          navigate("/dashboard");
        }
      } else if (data.status === "rate_limited") {
        const retryAfterSeconds = data.retryAfterSeconds || 60;
        const until = Date.now() + retryAfterSeconds * 1000;
        localStorage.setItem("loginLockoutUntil", String(until));
        setLockoutUntil(until);
        setLoginError(false);
        showToast("error", data.message || "Too many login attempts. Please try again later.", 3000);
      } else {
        if (!isMountedRef.current) return;
        showToast("error", data.message, 3000);
        setLoginError(true);
      }
    } catch (error) {
      // console.error("Error logging in:", error);
      if (!isMountedRef.current) return;
      showToast("error", "Unexpected error occurred. Please try again.", 3000);
      setLoginError(true);
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  return (
    <div
      className="min-h-screen text-gray-900 flex justify-center"
      style={{
        backgroundColor: theme.colors.background.secondary,
        color: theme.colors.text.primary,
      }}
    >
      <div
        className="max-w-screen-xl w-1/2 m-0 sm:m-10 bg-white shadow sm:rounded-lg flex justify-center flex-1"
        style={{ backgroundColor: "transparent" }}
      >
        <div className="w-1/2 p-6 flex flex-col justify-center items-center">
          <div>
            <img src={appLogo} className="w-32 mx-auto" alt="logo" />
          </div>
          <div className="mt-12 flex flex-col items-center">
            <h1 className="text-2xl xl:text-3xl font-extrabold">
              Login to your Account
            </h1>
            <div className="w-full flex-1 mt-8">
              <div className="mx-auto max-w-xs">
                {validationErrors.pid && (
                  <div className="text-red-500 text-sm mb-1">
                    {validationErrors.pid}
                  </div>
                )}
                <div className="text-xs mb-1 ml-1 text-gray-500">PID</div>
                <input
                  className={`w-full px-8 py-4 mb-5 rounded-full font-medium placeholder-gray-500 text-xs focus:outline-none focus:border-gray-400 focus:bg-white disabled:cursor-not-allowed disabled:opacity-60 ${
                    validationErrors.pid ? "border-red-500" : "border-gray-200"
                  }`}
                  type="text"
                  placeholder="Enter your PID"
                  onChange={(e) =>
                    setFormData({ ...formData, pid: e.target.value })
                  }
                  disabled={isLoading || isLockedOut}
                  style={{
                    backgroundColor: theme.colors.background.primary,
                    color: theme.colors.text.primary,
                    border: `1px solid ${theme.colors.background.accent}`,
                  }}
                />
                

                {validationErrors.password && (
                  <div className="text-red-500 text-sm mb-1">
                    {validationErrors.password}
                  </div>
                )}
                <div className="text-xs mb-1 ml-1 text-gray-500">Password</div>
                <input
                  className={`w-full px-8 py-4 mb-5 rounded-full font-medium placeholder-gray-500 text-xs focus:outline-none focus:border-gray-400 focus:bg-white disabled:cursor-not-allowed disabled:opacity-60 ${
                    validationErrors.password
                      ? "border-red-500"
                      : "border-gray-200"
                  }`}
                  type="password"
                  placeholder="*********"
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  disabled={isLoading || isLockedOut}
                  style={{
                    backgroundColor: theme.colors.background.primary,
                    border: `1px solid ${theme.colors.background.accent}`,
                  }}
                />

                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {(() => {
                      const isSubmitDisabled = isLoading || isLockedOut || formData.pid === "" || formData.password === "";
                      return (
                    <img
                      src={signInBtn}
                      alt="Sign in Button"
                      className={`h-8 ${isSubmitDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}
                      onClick={isSubmitDisabled ? undefined : handleSubmit}
                      style={{
                        filter: `${isSubmitDisabled ? "grayscale(1)" : ""}`,
                      }}
                    />
                      );
                    })()}
                    {isLoading && (
                      <BiLoaderCircle
                        className="animate-spin text-center ml-3"
                        color="#9999FF"
                        size={20}
                      />
                    )}
                    <button
                      className="text-xs px-4 py-2 ml-5 rounded-full disabled:cursor-not-allowed disabled:opacity-60"
                      style={{ background: theme.colors.background.buttonBg }}
                      onClick={() => {
                        setShowPasswordResetPanel(true);
                      }}
                      disabled={isLockedOut}
                    >
                      Forgot Password
                    </button>
                  </div>
                </div>

                {isLockedOut && (
                  <div className="text-yellow-500 text-sm mt-4">
                    Too many attempts. Try again in {formatRemaining(remainingSeconds)}.
                  </div>
                )}

                {loginError && (
                  <div className="text-red-500 text-sm mt-4">
                    Incorrect PID or password. Please try again.
                  </div>  
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {showPasswordResetPanel && (
        <PasswordResetRequestPanel
          setShowPasswordResetPanel={setShowPasswordResetPanel}
        />
      )}
    </div>
  );
};

export default SignInPage;